// Artifact reducer extracted from sessionStore.ts (docs/plans/13 §M3).
// Maps a `tool_result` event onto the ArtifactState shape — this is
// where each tool's structured payload becomes a typed artifact the
// right-panel views can render.

import type { ArtifactState } from "@/stores/sessionStore.types";

export function pickArtifactsFromTool(
  state: ArtifactState,
  toolName: string,
  result: Record<string, unknown>,
  args: Record<string, unknown>,
): ArtifactState {
  switch (toolName) {
    case "ExtractEntities": {
      // Tool returns { extracted: bool, entities: {...} } on success
      const entities = result.entities;
      if (result.extracted === true && typeof entities === "object" && entities !== null) {
        return { ...state, denialDetails: entities as Record<string, unknown> };
      }
      return state;
    }
    case "PredictSuccess":
      // Tool returns the probability payload directly
      return { ...state, probability: result };
    case "SearchPolicies": {
      const hits = Array.isArray(result.hits) ? (result.hits as Record<string, unknown>[]) : null;
      if (!hits) return state;
      return { ...state, policyHits: hits };
    }
    case "PlanEvidence": {
      // Backend returns {ok, plan: {...}, ...}. Park the plan so the
      // Evidence tab can show goals + planned searches.
      if (result.ok === false) return state;
      const plan = result.plan;
      if (typeof plan !== "object" || plan === null) return state;
      return { ...state, evidencePlan: plan as Record<string, unknown> };
    }
    case "AgentTool": {
      // The meta-tool that spawns specialists. Capture the structured
      // findings per specialist so the Evidence tab can render them.
      if (result.ok === false) return state;
      const specialist = typeof result.specialist === "string" ? result.specialist : null;
      const findings =
        typeof result.findings === "object" && result.findings !== null
          ? (result.findings as Record<string, unknown>)
          : null;
      if (!specialist || !findings) return state;
      if (specialist === "policy_researcher") {
        return { ...state, policyResearcherFindings: findings };
      }
      if (specialist === "medical_record_extractor") {
        return { ...state, medicalRecordFindings: findings };
      }
      return state;
    }
    case "SearchMedicalRecord": {
      // Newest first; cap at 30 entries. Tag each hit with the query
      // that produced it so the Evidence tab can answer "why was this
      // chunk retrieved?" instead of dumping anonymous OCR text.
      const hits = Array.isArray(result.hits) ? (result.hits as Record<string, unknown>[]) : [];
      if (hits.length === 0) return state;
      const query = typeof args.query === "string" ? args.query : "";
      const stamped = hits.map((h) => ({
        ...h,
        _query: query,
        _capturedAt: new Date().toISOString(),
      }));
      return {
        ...state,
        medicalRecordSearchHits: [...stamped, ...state.medicalRecordSearchHits].slice(0, 30),
      };
    }
    case "GenerateAppealSection": {
      // Tool returns the rendered section: {section_type, title, body, citations}.
      // Key by section_type so re-generation REPLACES (no duplicate cards).
      // A section that couldn't be drafted (no medical record) comes back as
      // {ok:false, skipped:true, ...} and must NOT become a letter card —
      // otherwise the old "insufficient evidence" placeholder reappears as a
      // blank/broken section.
      if (result.ok === false || result.skipped === true) return state;
      const sectionType = typeof result.section_type === "string" ? result.section_type : null;
      if (!sectionType) return state;
      return {
        ...state,
        appealSections: { ...state.appealSections, [sectionType]: result },
        // The letter changed → the last verification snapshot is stale.
        lastVerification: null,
      };
    }
    case "RefineAppealSection": {
      // Backend stages the refinement on session.pending_refinements; tool
      // returns {section_type, proposed_body, diff_summary, status:"pending"}.
      const sectionType = typeof result.section_type === "string" ? result.section_type : null;
      if (!sectionType || result.status !== "pending") return state;
      return {
        ...state,
        pendingRefinements: { ...state.pendingRefinements, [sectionType]: result },
      };
    }
    case "AcceptRefinement": {
      // Result: {ok, section_type, status:"accepted"}. Move the pending
      // proposed body into appealSections[section_type] and clear pending.
      const sectionType = typeof result.section_type === "string" ? result.section_type : null;
      if (!sectionType || result.ok === false) return state;
      const pending = state.pendingRefinements[sectionType];
      const proposedBody =
        pending && typeof pending.proposed_body === "string" ? pending.proposed_body : null;
      const existing = state.appealSections[sectionType] ?? {};
      const nextPending = { ...state.pendingRefinements };
      delete nextPending[sectionType];
      return {
        ...state,
        appealSections: {
          ...state.appealSections,
          [sectionType]: proposedBody !== null ? { ...existing, body: proposedBody } : existing,
        },
        pendingRefinements: nextPending,
        // The accepted body invalidates the last verification snapshot.
        lastVerification: null,
      };
    }
    case "RevertRefinement": {
      const sectionType = typeof result.section_type === "string" ? result.section_type : null;
      if (!sectionType || result.ok === false) return state;
      const nextPending = { ...state.pendingRefinements };
      delete nextPending[sectionType];
      return { ...state, pendingRefinements: nextPending };
    }
    case "VerifyCitations": {
      const next = {
        ...state,
        references: Array.isArray(result.references)
          ? (result.references as Record<string, unknown>[])
          : state.references,
      };
      if (result.ok === false || typeof result.verified !== "boolean") return next;
      // failed_sections is authoritative when present; fall back to
      // filtering the per-section reports for older payloads.
      const failedSections = Array.isArray(result.failed_sections)
        ? result.failed_sections.filter((s): s is string => typeof s === "string")
        : Array.isArray(result.sections)
          ? (result.sections as Record<string, unknown>[])
              .filter((s) => s.verified !== true && typeof s.section_type === "string")
              .map((s) => s.section_type as string)
          : [];
      return {
        ...next,
        lastVerification: { verified: result.verified, failedSections },
      };
    }
    case "ExportAppealPdf":
      return {
        ...state,
        exports: {
          ...state.exports,
          pdf: typeof result.url === "string" ? result.url : state.exports.pdf,
        },
      };
    case "ExportAppealDocx":
      return {
        ...state,
        exports: {
          ...state.exports,
          docx: typeof result.url === "string" ? result.url : state.exports.docx,
        },
      };
    default:
      return state;
  }
}
